import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { PerfilEquipo } from "../../../services/v2/prospects.service";
import { formatPhoneDisplay } from "../../../components/phoneField";
import WappIcono from "../../../components/wappIcono";
import { useFotos } from "../../../appMovil/fotos";
import { errorTexto, iniciales, tonoAvatar } from "./salesHelpers";
import { llamarEnEquipo } from "./llamadasEquipo";
import { copiarTexto } from "./seleccion";
import { IconoCanal } from "./iconoCanalPicker";

const http = HttpManager.getInstance();
/** Siempre con lada (+504 / +52), como pide Esteban para todo número que se copie (2026-09-30). */
const conLada = (t: string | null): string => (t ? formatPhoneDisplay(t, "0") || t : "");

/** "Activa ahora", "Activa hace 3 h", "Activa ayer"… desde el último uso de su sesión (se toca cada 15 min). */
export const actividad = (p: Pick<PerfilEquipo, "enLinea" | "ultimoUso">): string => {
	if (p.enLinea) return "Activa ahora";
	if (!p.ultimoUso) return "Sin actividad reciente";
	const d = new Date(p.ultimoUso.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(p.ultimoUso) ? "" : "Z"));
	const min = Math.round((Date.now() - d.getTime()) / 60000);
	if (isNaN(min)) return "Sin actividad reciente";
	if (min < 60) return "Activa hace " + min + " min";
	if (min < 24 * 60) return "Activa hace " + Math.round(min / 60) + " h";
	const dias = Math.round(min / 1440);
	return dias === 1 ? "Activa ayer" : dias <= 30 ? "Activa hace " + dias + " días" : "Sin actividad reciente";
};

/** Dónde se tocó: la tarjeta sale junto a ese punto en computadora; en celular sale como hoja abajo. */
/** userId: id de WAPP, o "z:<id de Zoho>" para las dueñas que vienen del CRM (WappChat). */
export interface AnclaPerfil { userId: number | string; x: number; y: number }

interface Props {
	campusId: number;
	ancla: AnclaPerfil;
	onCerrar: () => void;
	/** Abrir (o crear) el directo con esta persona. */
	onMensaje: (userId: number) => void;
	/** Abrir una conversación en común. */
	onAbrirCanal: (canalId: number) => void;
}

/**
 * Tarjeta de perfil como Slack y WhatsApp (Esteban, 2026-10-25: "no puedes click en el perfil de las personas"):
 * foto, nombre, si está activa, roles por plantel, correo, conversaciones en común y Mensaje / Llamar.
 */
const PerfilPersona: React.FC<Props> = ({ campusId, ancla, onCerrar, onMensaje, onAbrirCanal }) => {
	const fotos = useFotos();
	const [p, setP] = useState<PerfilEquipo | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [copiado, setCopiado] = useState<string | null>(null);
	const [llamando, setLlamando] = useState(false);
	const caja = useRef<HTMLDivElement>(null);
	const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
	const celular = window.matchMedia("(max-width: 700px)").matches;

	useEffect(() => {
		let vivo = true;
		setP(null);
		setError(null);
		http.prospectsService.equipoPerfil(campusId, ancla.userId)
			.then((r) => { if (vivo) setP(r.data); })
			.catch((e) => { if (vivo) setError(errorTexto(e)); });
		return () => { vivo = false; };
	}, [campusId, ancla.userId]);

	useEffect(() => {
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, [onCerrar]);

	// A la derecha del clic si cabe, si no a la izquierda; nunca fuera de la pantalla.
	useLayoutEffect(() => {
		if (celular || !caja.current) return;
		const r = caja.current.getBoundingClientRect();
		const m = 12;
		let left = ancla.x + 14;
		if (left + r.width > window.innerWidth - m) left = Math.max(m, ancla.x - r.width - 14);
		const top = Math.min(Math.max(m, ancla.y - 40), window.innerHeight - r.height - m);
		setPos({ left, top });
	}, [ancla.x, ancla.y, celular, p, error]);

	const copiar = async (t: string, que: string) => {
		if (await copiarTexto(t)) {
			setCopiado(que);
			setTimeout(() => setCopiado(null), 1500);
		}
	};
	const llamar = async () => {
		if (!p) return;
		setLlamando(true);
		try {
			const id = p.directoId ?? (await http.prospectsService.equipoDirecto(campusId, p.id)).data.id;
			const e = await llamarEnEquipo(campusId, id);
			if (e) setError(e);
			else onCerrar();
		} catch (e) {
			setError(errorTexto(e));
		}
		setLlamando(false);
	};

	const nombre = p?.nombre || "";
	const partes = nombre.split(" ");
	return (
		<>
			<div className="pp-velo" onClick={onCerrar} aria-hidden="true" />
			<div ref={caja} className={"pp-tarjeta" + (celular ? " hoja" : "")} role="dialog" aria-label={nombre ? "Perfil de " + nombre : "Perfil"}
				style={celular ? undefined : pos ? { left: pos.left, top: pos.top } : { visibility: "hidden", left: 0, top: 0 }}>
				<button type="button" className="pp-x" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button>
				{error && !p ? <div className="pp-error">{error}</div> : !p ? <div className="pp-cargando">Cargando…</div> : (
					<>
						<div className="pp-cab">
							<span className={"pp-foto " + tonoAvatar(p.id) + (fotos[p.id] ? " con-foto" : "")} aria-hidden="true">
								{fotos[p.id] ? <img src={fotos[p.id]} alt="" /> : iniciales({ first_name: partes[0], last_name: partes[1] || "" })}
								{p.activa ? <i className={"pp-punto" + (p.enLinea ? " on" : "")} /> : null}
							</span>
							<div className="pp-quien">
								<b className="pp-nombre">{p.nombre}{p.esYo ? <span className="pp-tu"> (tú)</span> : null}</b>
								<span className={"pp-act" + (p.enLinea ? " on" : "")}>{p.activa ? actividad(p) : "Ya no está activa en WAPP"}</span>
								{p.estado?.emoji || p.estado?.texto ? <span className="pp-estado">{p.estado.emoji ? p.estado.emoji + " " : ""}{p.estado.texto}</span> : null}
								{p.estado?.noMolestarHasta ? <span className="pp-pausa">Avisos pausados</span> : null}
							</div>
						</div>
						{p.roles.length ? (
							<ul className="pp-roles">
								{p.roles.map((r) => <li key={r.rol}><b>{r.rol}</b>{r.planteles.length ? <span>{r.planteles.join(", ")}</span> : null}</li>)}
							</ul>
						) : null}
						{!p.esYo && p.activa ? (
							<div className="pp-botones">
								<button type="button" className="btn-wa pri" onClick={() => onMensaje(p.id)}><WappIcono n="message" />Mensaje</button>
								<button type="button" className="btn-wa" onClick={llamar} disabled={llamando}><WappIcono n="phone" />{llamando ? "Llamando…" : "Llamar"}</button>
							</div>
						) : null}
						{p.correo || p.telefono ? (
							<dl className="pp-datos">
								{p.correo ? (<><dt>Correo</dt>
									<dd><a href={"mailto:" + p.correo}>{p.correo}</a>
										<button type="button" className="pp-copiar" onClick={() => copiar(p.correo!, "correo")} title="Copiar correo" aria-label="Copiar correo">
											{copiado === "correo" ? "Copiado" : <WappIcono n="copy" />}</button></dd></>) : null}
								{p.telefono ? (<><dt>Teléfono</dt>
									<dd><a href={"tel:+" + p.telefono.replace(/\D/g, "")}>{conLada(p.telefono)}</a>
										<button type="button" className="pp-copiar" onClick={() => copiar(conLada(p.telefono), "tel")} title="Copiar teléfono" aria-label="Copiar teléfono">
											{copiado === "tel" ? "Copiado" : <WappIcono n="copy" />}</button></dd></>) : null}
							</dl>
						) : null}
						{!p.esYo && p.comunes.length ? (
							<div className="pp-comunes">
								<small>En común</small>
								<div>{p.comunes.map((c) => (
									<button type="button" key={c.id} className="pp-chip" onClick={() => onAbrirCanal(c.id)}>
										{c.icono ? <IconoCanal id={c.id} icono={c.icono} /> : c.tipo === "canal" ? "#" : <WappIcono n="users" />}<span>{c.nombre || "Grupo"}</span>
									</button>
								))}</div>
							</div>
						) : null}
						{error ? <div className="pp-error">{error}</div> : null}
					</>
				)}
			</div>
		</>
	);
};

export default PerfilPersona;
